import React from 'react';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useTranslation } from 'react-i18next';
import { getClientTimezone, stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { partiallyMaskValue } from '@dxb-web-ui-framework/common/utils/DataObfuscation';
import { Box, makeStyles } from '@dxb-web-ui-framework/design-system';
import { TransactionOrderStatusChip } from '../../../transaction/src/components';
import { TableGridConfig } from '../../../content-composer/src/components/Generic/types';
import {
  formatAmountCellRenderer,
  stringToDateCellRenderer,
  stringWithPartiallyMaskValue,
  transferHistoryTransactionTypeCellRenderer,
  viewDetailsButtonRenderer,
} from '../../../content-composer/src/components/Generic/TableGrid/tableGridCellRenderers/tableGridCellRenderers';
import {
  formatAmountTransformer,
  transactionStatusTransformer,
} from '../../../content-composer/src/components/Generic/TableGrid/tableGridMobileListTransformers/tableGridMobileListTransformers';

const useStyles = makeStyles(() => ({
  root: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '10ch',
  },
}));

// TODO: get date format from the translation based on selected locale
const useTransactionsGridConfig = (onToggleDrawer: Function): TableGridConfig => {
  const { t } = useTranslation();
  const locale = t('global.settings.date.locale');
  const dateFormat = t('global.settings.date.format');
  const timezone = getClientTimezone();
  const classes = useStyles();

  const gridConfig: TableGridConfig = {
    gridOptions: {
      ariaTitle: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.AriaTitle'),
      dataTestId: 'Transactions-table',
      noResultsMsg: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.NoResultsMsg'),
      padding: 'normal',
      size: 'medium',
      stickyHeader: false,
      collapseToList: true,
    },
    columnDefs: [
      {
        id: 'Creation date',
        field: 'orderTrxDate',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingCreationDate'),
        cellRenderer: stringToDateCellRenderer(dateFormat, locale, timezone),
      },
      {
        id: 'Value date',
        field: 'orderValueDate',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingValueDate'),
        cellRenderer: stringToDateCellRenderer(dateFormat, locale, timezone),
      },
      {
        id: 'Recipient',
        field: 'beneficiaryName',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingCounterparty'),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      {
        id: 'BIC',
        field: 'beneficiaryBic',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingBeneficiaryBic'),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      {
        id: 'Bank',
        field: 'beneficiaryBank',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingBeneficiaryBank'),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      {
        id: 'Status',
        field: 'status',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingStatus'),
        cellRenderer: transactionStatusTransformer,
      },
      {
        id: 'Payment details',
        field: 'orderComments',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingPaymentDetails'),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      {
        id: 'Account',
        field: 'beneficiaryAccount',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingAccount'),
        cellRenderer: stringWithPartiallyMaskValue(4, 5),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      // TODO use genericChipCellRenderer from develop branch for displaying chip
      {
        id: 'Type',
        field: 'orderDirection',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingTransactionType'),
        cellRenderer: (rowData: any, columnDef: any) =>
          transferHistoryTransactionTypeCellRenderer(rowData[columnDef.field], t),
      },
      {
        id: 'Transaction ID',
        field: 'orderId',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingTransactionId'),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      {
        id: 'Amount',
        field: 'orderAmount',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingAmount'),
        columnHeaderCellProps: {
          align: 'right',
        },
        cellProps: {
          align: 'right',
        },
        cellRenderer: formatAmountCellRenderer(locale, true, 'currencyShortDescription'),
      },
      {
        id: 'View Icon',
        field: 'actions',
        label: '',
        cellProps: { align: 'right', classes: { root: classes.root } },
        cellRenderer: viewDetailsButtonRenderer(
          onToggleDrawer,
          'orderId',
          false,
          t('accountsDetailsPage.RecentAccountExtraitTransactions.VisibilityIconBtnTitle'),
          t('accountsDetailsPage.RecentAccountExtraitTransactions.VisibilityIconBtnAriaLabel'),
        ),
      },
    ],
    mobileListConfig: {
      listItemContents: [
        {
          label: 'beneficiaryName',
          value: 'orderTrxDate',
          valueAreaLabel: 'orderTrxDate',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          valueTransformer: (data: GenericObject) => (
            <Box mt={1}>
              {partiallyMaskValue(data.beneficiaryAccount.slice(-5), 4)}
              <Box mt={1}>
                {`${stringToDate({
                  date: data.orderTrxDate,
                  formatExp: dateFormat,
                  localeName: locale,
                  tz: timezone,
                })}`}
              </Box>
            </Box>
          ),
        },
        {
          label: 'orderAmount',
          value: 'status',
          valueAreaLabel: 'status',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          labelAlignment: 'right',
          valueAlignment: 'right',
          labelTransformer: formatAmountTransformer('orderAmount', 'currencyShortDescription', 2, locale),
          valueTransformer: (data: GenericObject) => (
            <Box mt={3} display='flex' justifyContent='flex-end'>
              <TransactionOrderStatusChip statusSerialNumber={data?.status} />
            </Box>
          ),
        },
      ],
      listItemOnSelect: rowData => onToggleDrawer(rowData.orderId),
    },
  };
  return gridConfig;
};

export { useTransactionsGridConfig };
